<template>
  <li
    :id="id"
    ref="innerRef"
    v-tippy="{
      content: tooltipContent,
      placement: 'top',
      offset: [0, 16],
    }"
    class="mx-1 mb-0.5 flex h-8 items-center rounded-sm outline-none last:mb-0"
    :class="[
      MENU_ITEM_CLASS,
      {
        'focus-within:bg-active-secondary hover:bg-active-secondary focus:bg-active-secondary': !disabled && selectable,
        'bg-active-secondary': (!checkbox && selected) || isFocused,
        'h-auto': autoHeight,
        'cursor-not-allowed bg-transparent text-gray-50 hover:bg-transparent': disabled,
        'pointer-events-none bg-transparent hover:bg-transparent': !selectable,
      },
    ]"
  >
    <div class="flex h-full w-full items-center justify-between overflow-hidden">
      <div class="flex h-full grow overflow-auto">
        <ui-checkbox
          v-if="checkbox"
          :checked="selected"
          :indeterminate="indeterminate"
          class="w-full px-2"
          :disabled="disabled"
          tabindex="-1"
          @change="$emit('check')"
        >
          <ui-menu-item-content data-testid="dropdown-item-label" :theme :truncate="truncateContent">
            <template v-if="$slots.icon" #icon>
              <slot name="icon" />
            </template>
            <slot />
          </ui-menu-item-content>
        </ui-checkbox>
        <button
          v-else
          v-bind="buttonAttrs"
          class="flex grow items-center gap-1 overflow-hidden text-left text-sm font-medium outline-none"
          :class="[$slots['option-icon'] ? 'px-2' : 'px-3', { 'cursor-not-allowed': disabled }]"
          type="button"
          @click="$emit('select', $event)"
        >
          <ui-menu-item-content
            :disabled="disabled"
            data-testid="dropdown-item-label"
            :theme
            :truncate="truncateContent"
          >
            <template v-if="$slots.icon" #icon>
              <slot name="icon" />
            </template>
            <slot />
          </ui-menu-item-content>
          <check-icon v-if="selected" class="shrink-0 text-active-primary icon-m" />
        </button>
      </div>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import { ref, inject, computed, watch } from 'vue'

import { CheckIcon } from '@klausapp/ui-kit/icons'

import UiCheckbox from '../UiCheckbox.vue'
import UiMenuItemContent from './UiMenuItemContent.vue'
import { UiMenuItemTheme, type MenuState } from './types'
import { MENU_ITEM_CLASS } from './constants'

withDefaults(
  defineProps<{
    tooltipContent?: string
    selectable?: boolean
    disabled?: boolean
    autoHeight?: boolean
    checkbox?: boolean
    selected?: boolean
    indeterminate?: boolean
    theme?: UiMenuItemTheme
    buttonAttrs?: Record<string, unknown>
    truncateContent?: boolean
  }>(),
  {
    tooltipContent: '',
    selectable: true,
    theme: UiMenuItemTheme.Default,
    buttonAttrs: () => ({}),
    truncateContent: true,
  },
)

const emit = defineEmits<{
  check: []
  select: [e?: MouseEvent | KeyboardEvent]
}>()

const id = ref(uuid())
const innerRef = ref<HTMLLIElement>()

const menuState = inject<MenuState>('menuState')

const isFocused = computed(() => menuState?.activeItemId.value === id.value)

const shouldSelect = computed(() => menuState?.itemIdToSelect?.value === id.value)

watch(isFocused, (focused) => {
  if (focused) {
    innerRef.value?.scrollIntoView({ inline: 'center', block: 'center' })
  }
})

watch(shouldSelect, (shouldSelect) => {
  if (shouldSelect) {
    emit('select')
    menuState?.resetSelectedItem()
  }
})

defineExpose({ innerRef })
</script>
